import { useEffect, useState } from "react";
import { api, formatINR, formatINRAxis, formatINRShort, formatShort, formatDate } from "@/lib/api";
import { PageHeader, StatusBadge, LotTag, QueueNo, UrgentBadge, DEPT_STATUSES, badgeStatus } from "@/components/Shared";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { canAccess, viewFor } from "@/config/roleViews";
import JobLink from "@/components/JobLink";
import { TrendingUp, TrendingDown, AlertTriangle, Package, IndianRupee, ClipboardList, UserMinus, ArrowRight, Truck, CalendarClock, Search, Printer, Boxes, ClipboardCheck, Scissors, ShieldCheck, PackageCheck } from "lucide-react";
import { BarChart, Bar, LabelList, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, CartesianGrid } from "recharts";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import StoresDashboard from "@/components/stores/StoresDashboard";
import ProductionAnalytics from "@/components/dashboard/ProductionAnalytics";
import QcMaterials from "@/components/stores/QcMaterials";
import PartialDispatch from "@/components/dashboard/PartialDispatch";
import JobStatusTile from "@/components/dashboard/JobStatusTile";
import MaterialsQcTile from "@/components/dashboard/MaterialsQcTile";
import { useLiveRefresh } from "@/lib/live";

function KPI({ label, value, exact, sub, icon: Icon, accent, testId, to }) {
  const body = (
    <div className={`border border-neutral-200 bg-white px-3 py-2.5 md:px-4 md:py-3 h-full ${to ? "hover:border-[#FF5A00] transition-colors" : ""}`} data-testid={testId} title={exact}>
      <div className="flex items-start justify-between">
        <div className="label-caps text-[10px] leading-tight">{label}</div>
        {Icon && <Icon size={13} strokeWidth={1.75} className="text-neutral-400 shrink-0" />}
      </div>
      <div className={`font-mono-num text-lg md:text-xl font-bold mt-1.5 tracking-tight ${accent || "text-neutral-900"}`}>
        {value}
      </div>
      {sub && <div className="text-[11px] text-neutral-500 mt-0.5 truncate">{sub}</div>}
    </div>
  );
  return to ? <Link to={to} className="block h-full">{body}</Link> : body;
}

// Amount above a bar as plain SVG text: recharts' own labels wrap on narrow bars.
function barValue({ x, y, width, value }) {
  if (!value) return null;
  return (
    <text x={x + width / 2} y={y - 5} textAnchor="middle" fontSize={10} fill="#3F3F46" className="font-mono-num">
      {formatINRShort(value)}
    </text>
  );
}

function Card({ title, right, children, testId, className = "" }) {
  return (
    <div className={`border border-neutral-200 bg-white ${className}`} data-testid={testId}>
      <div className="px-5 py-4 border-b border-neutral-200 flex justify-between items-center">
        <div className="label-caps">{title}</div>
        {right}
      </div>
      {children}
    </div>
  );
}

// A list of jobs in one status (used by the finer desks: QC, dispatch, shift board)
// Jobs waiting on this status are mid this Production Flow stage — clicking one should land straight on it.
// Each status is the one set when that stage's own prerequisite finishes (see production_flow.STAGES'
// prev-stage chain and STAGE_JOB_STATUS on the backend, which this mirrors by hand).
const STAGE_FOR_STATUS = {
  in_production: "printing", partially_printed: "printing",
  printing: "avt", flat_bed: "avt",
  avt: "slitting",
  slitting: "quality",
  qc: "packing",
  packing: "coa",
  ready_to_dispatch: "dispatch",
};

function JobsByStatus({ status, title, readOnly = false }) {
  const { user } = useAuth();
  const canBrowseOrders = canAccess(user, "/orders");
  const [rows, setRows] = useState(null);
  const [total, setTotal] = useState(null);
  const stage = STAGE_FOR_STATUS[status.split(",")[0]];
  const load = () => {
    // The top of the department's queue, in its order and with each job's number — same as JobStatusTile's dialog.
    api.get("/jobs", { params: { ready: status, limit: 8 } })
      .then(({ data }) => setRows(data))
      .catch(() => setRows((r) => r || []));
    api.get("/jobs", { params: { ready: status, limit: 1, with_total: true } }).then(({ data }) => setTotal(data.total)).catch(() => {});
  };
  useEffect(() => { load(); }, [status]); // eslint-disable-line react-hooks/exhaustive-deps
  useLiveRefresh("jobs", load);
  const Row = readOnly ? "div" : Link;
  const rowProps = (j) => readOnly ? {} : { to: stage ? `/orders/${j.id}?tab=production_flow&stage=${stage}` : `/orders/${j.id}` };
  return (
    <Card title={total != null ? `${title || status} (${total.toLocaleString("en-IN")})` : (title || status)} testId={`widget-jobs-${status}`}
      right={!readOnly && canBrowseOrders && <Link to={`/orders?ready=${encodeURIComponent(status)}`} className="text-xs text-[#FF5A00] hover:underline">Open Orders →</Link>}>
      <div className="divide-y divide-neutral-100">
        {(rows || []).map((j) => (
          <Row key={j.id} {...rowProps(j)} className={`px-5 py-3 flex justify-between items-center ${readOnly ? "" : "hover:bg-neutral-50"} ${j.urgent_schedule ? "bg-rose-50/70" : ""}`}>
            <div>
              <div className="font-mono-num text-xs flex items-center gap-1.5">
                <QueueNo n={j.queue_no} />
                <UrgentBadge urgent={!!j.urgent_schedule} />
                {j.mpwo_number || j.job_number} <LotTag job={j} />
              </div>
              <div className="text-sm">{j.customer_name}</div>
            </div>
            <div className="text-xs text-neutral-500 truncate max-w-[40%] text-right">
              {j.product_name}
              {status === "partially_printed" && j.passed_qty > 0 && (
                <div className="font-mono-num text-amber-700">{Number(j.passed_qty).toLocaleString("en-IN")} passed on · {Number(j.quantity || 0).toLocaleString("en-IN")} left to print</div>
              )}
              {status === "partially_printed" && !(j.passed_qty > 0) && j.printed_qty > 0 && (
                <div className="font-mono-num text-amber-700">{Number(j.printed_qty).toLocaleString("en-IN")} of {Number(j.quantity || 0).toLocaleString("en-IN")} printed</div>
              )}
            </div>
          </Row>
        ))}
        {rows && rows.length === 0 && <div className="px-5 py-6 text-center text-neutral-400 text-sm">Nothing in this state.</div>}
        {!rows && <div className="px-5 py-6 text-center text-neutral-400 text-sm">Loading…</div>}
      </div>
    </Card>
  );
}

function OperatorRoster() {
  const [rows, setRows] = useState(null);
  const load = () => api.get("/operators").then(({ data }) => setRows(data)).catch(() => setRows((r) => r || []));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useLiveRefresh("masters", load);
  const active = (rows || []).filter((o) => (o.status || "active") === "active").length;
  return (
    <Card title={`Operator roster${rows ? ` · ${active} active of ${rows.length}` : ""}`} testId="widget-operator-roster">
      <div className="divide-y divide-neutral-100 max-h-80 overflow-auto">
        {(rows || []).map((o) => (
          <div key={o.id || o.code} className="px-5 py-3 flex justify-between items-center">
            <div>
              <div className="text-sm font-medium">{o.name}</div>
              <div className="text-xs text-neutral-500 font-mono-num">{o.code}</div>
            </div>
            <div className="text-xs text-neutral-500">{o.skill || "—"} · {o.status || "active"}</div>
          </div>
        ))}
        {rows && rows.length === 0 && <div className="px-5 py-6 text-center text-neutral-400 text-sm">No operators yet.</div>}
      </div>
    </Card>
  );
}

function CalibrationDue() {
  const [d, setD] = useState(null);
  const load = () => api.get("/equipment/calibration").then(({ data }) => setD(data)).catch(() => setD((x) => x || { overdue: [], due_soon: [], dated: 0, without_date: 0 }));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useLiveRefresh("masters", load);
  if (!d) return null;
  const rows = [...d.overdue, ...d.due_soon];
  return (
    <Card title={`Calibration due${rows.length ? ` · ${d.overdue.length} overdue, ${d.due_soon.length} due within ${d.window_days} days` : ""}`} testId="widget-calibration-due">
      <div className="divide-y divide-neutral-100 max-h-72 overflow-auto">
        {rows.map((e) => (
          <Link key={e.id} to="/equipment" className="px-5 py-2.5 flex justify-between items-center gap-3 hover:bg-neutral-50">
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">{e.name}</div>
              <div className="text-xs text-neutral-500 font-mono-num">{e.code || e.equip_type}{e.location ? ` · ${e.location}` : ""}</div>
            </div>
            <div className={`text-xs font-mono-num shrink-0 ${e.days < 0 ? "text-rose-600" : "text-amber-700"}`}>
              {e.days < 0 ? `${-e.days} days overdue` : e.days === 0 ? "due today" : `in ${e.days} days`} · {e.next_calibration}
            </div>
          </Link>
        ))}
        {rows.length === 0 && (
          <div className="px-5 py-5 text-sm text-neutral-500" data-testid="calibration-empty">
            {d.dated ? "All calibrations are up to date." : "No calibration dates entered yet."}
            {d.without_date > 0 && <> {d.without_date} equipment have no next calibration date. <Link to="/equipment" className="underline">Add them on the Equipment page</Link>.</>}
          </div>
        )}
      </div>
    </Card>
  );
}

// Same bar as Orders' own filter row — jumps straight into a pre-filtered Orders list
// instead of filtering in place, since the dashboard has no job table of its own.
export function OrdersSearchBar() {
  const nav = useNavigate();
  const [statusList, setStatusList] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [inquiry, setInquiry] = useState("all");
  const [orderType, setOrderType] = useState("all");

  useEffect(() => {
    api.get("/jobs/stats/statuses").then(({ data }) => setStatusList(data)).catch(() => {});
  }, []);

  const go = () => {
    const params = new URLSearchParams();
    if (q.trim()) params.set("q", q.trim());
    if (status !== "all") params.set("status", status);
    if (inquiry !== "all") params.set("inquiry_type", inquiry);
    if (orderType !== "all") params.set("order_type", orderType);
    nav(`/orders${params.toString() ? `?${params}` : ""}`);
  };

  return (
    <div className="mb-2 flex flex-wrap gap-3 items-center" data-testid="widget-orders-search">
      <div className="relative w-full md:w-auto">
        <Search size={14} className="absolute left-2.5 top-2.5 text-neutral-400" />
        <Input
          data-testid="dashboard-orders-search"
          placeholder="Search BPR, PO, customer, product…"
          className="pl-8 rounded-sm w-full md:w-72"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && go()}
        />
      </div>
      <Select value={status} onValueChange={setStatus}>
        <SelectTrigger data-testid="dashboard-status-filter" className="w-full md:w-56 rounded-sm">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All statuses</SelectItem>
          {statusList.filter((s) => s.status).map((s) => (
            <SelectItem key={s.status} value={s.status}>
              {s.status.replace(/_/g, " ")} · {s.count.toLocaleString("en-IN")}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select value={inquiry} onValueChange={setInquiry}>
        <SelectTrigger data-testid="dashboard-inquiry-filter" className="w-full md:w-44 rounded-sm">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All inquiries</SelectItem>
          <SelectItem value="New Inquiry">New Inquiry</SelectItem>
          <SelectItem value="Repeat Inquiry">Repeat Inquiry</SelectItem>
        </SelectContent>
      </Select>
      <Select value={orderType} onValueChange={setOrderType}>
        <SelectTrigger data-testid="dashboard-order-type-filter" className="w-full md:w-40 rounded-sm">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All orders</SelectItem>
          <SelectItem value="New Order">New Order</SelectItem>
          <SelectItem value="Repeat Order">Repeat Order</SelectItem>
        </SelectContent>
      </Select>
      <Button variant="outline" onClick={go} className="rounded-sm">Apply</Button>
    </div>
  );
}

function QuickLinks({ links }) {
  if (!links || links.length === 0) return null;
  return (
    <section className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="widget-quick-links">
      {links.map((l) => (
        <Link key={l.to} to={l.to} className="border border-neutral-200 bg-white p-5 hover:border-[#FF5A00] transition-colors flex items-center justify-between">
          <div>
            <div className="font-display font-bold">{l.label}</div>
            <div className="text-xs text-neutral-500 mt-1">{l.hint}</div>
          </div>
          <ArrowRight size={18} className="text-[#FF5A00]" />
        </Link>
      ))}
    </section>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const view = viewFor(user);
  const cfg = view.dashboard;
  const widgets = cfg.widgets.map((w) => (typeof w === "string" ? { id: w } : w));
  const has = (id) => widgets.some((w) => w.id === id);
  const needsSummary = widgets.some((w) =>
    ["kpi_jobs", "kpi_revenue", "kpi_profit", "kpi_lowstock", "kpi_avg_invoice", "kpi_avg_job", "kpi_avg_label", "kpi_dispatched", "kpi_to_be_planned", "job_status", "trend", "recent_jobs", "low_stock"].includes(w.id));
  const needsChurn = has("churn");

  const [data, setData] = useState(null);
  const [churn, setChurn] = useState(null);

  // Land on the role's home page once per sign-in (e.g. stores keeper -> Inventory)
  useEffect(() => {
    if (view.landing && view.landing !== "/" && sessionStorage.getItem("erp_landed") !== user.role) {
      sessionStorage.setItem("erp_landed", user.role);
      nav(view.landing, { replace: true });
    }
    // eslint-disable-next-line
  }, [user.role]);

  const loadSummary = () => needsSummary && api.get("/dashboard/summary").then(({ data }) => setData(data)).catch(() => {});
  const loadChurn = () => needsChurn && api.get("/dashboard/churn-alert").then(({ data }) => setChurn(data)).catch(() => {});
  useEffect(() => {
    loadSummary();
    loadChurn();
    // eslint-disable-next-line
  }, [user.role]);
  // Job counts / statuses / revenue, low stock, and the customer churn figures stay current.
  useLiveRefresh(["jobs", "stores", "finance"], loadSummary, { enabled: needsSummary });
  useLiveRefresh(["jobs", "customers"], loadChurn, { enabled: needsChurn });

  if (needsSummary && !data) return <div className="p-8 label-caps">Loading…</div>;
  const d = data || {};
  const kpis = widgets.filter((w) => w.id.startsWith("kpi_"));
  const showFinance = view.finance;

  // Tiles, rows and statuses open the page behind them when this view can see it.
  const linkIf = (path, to = path) => (canAccess(user, path) ? to : undefined);
  const stockFor = (sku) => linkIf("/inventory", `/inventory?tab=stock&q=${encodeURIComponent(sku)}`);
  const deptCount = (dept) => DEPT_STATUSES[dept].statuses.reduce((s, k) => s + (d.status_counts?.[k] || 0), 0);
  // Counted on the server like the department queues: a Converted BPR job by its BPR Status line. The
  // lines are shown when more than one feeds a tile, so each can be checked against the Excel pivot.
  const deptTile = (dept, icon) => {
    const lines = d.dept_lines?.[dept] || [];
    return (
      <KPI label={`Jobs in ${DEPT_STATUSES[dept].label}`} value={d.dept_counts?.[dept] ?? deptCount(dept)} icon={icon} testId={`kpi-dept-${dept}`}
        sub={[d.dept_not_in_pipeline?.[dept] > 0 && `+ ${d.dept_not_in_pipeline[dept].toLocaleString("en-IN")} not in Pipeline`,
          lines.length > 1 && lines.map((l) => `${l.n.toLocaleString("en-IN")} ${l.label}`).join(" · ")].filter(Boolean).join(" · ") || undefined}
        to={linkIf("/orders", `/orders?dept=${dept}`)} />
    );
  };

  const KPI_DEFS = {
    kpi_jobs: () => <KPI label="Total Jobs" value={d.total_jobs} sub={d.jobs_not_in_pipeline ? `incl. ${d.jobs_not_in_pipeline.toLocaleString("en-IN")} not in Jobs Pipeline` : undefined} icon={ClipboardList} testId="kpi-total-jobs" to={linkIf("/orders")} />,
    kpi_revenue: () => <KPI label="Revenue" value={formatINRShort(d.total_revenue)} exact={formatINR(d.total_revenue)} icon={IndianRupee} testId="kpi-revenue" to={linkIf("/sales")} />,
    kpi_profit: () => (
      <KPI
        label="Profit"
        value={formatINRShort(d.total_profit)}
        exact={formatINR(d.total_profit)}
        sub={`${d.margin_pct}% margin`}
        icon={d.total_profit >= 0 ? TrendingUp : TrendingDown}
        accent={d.total_profit >= 0 ? "text-emerald-700" : "text-rose-700"}
        testId="kpi-profit"
        to={linkIf("/reports")}
      />
    ),
    kpi_avg_invoice: () => <KPI label="Avg value / Invoice" value={formatINRShort(d.avg_invoice_value)} exact={d.avg_invoice_value != null ? formatINR(d.avg_invoice_value) : undefined} testId="kpi-avg-invoice" to={linkIf("/sales")} />,
    kpi_avg_job: () => <KPI label="Avg value / Job" value={formatINRShort(d.avg_job_value)} sub={`${Number(d.jobs_billed || 0).toLocaleString("en-IN")} jobs billed`} testId="kpi-avg-job" to={linkIf("/sales")} />,
    kpi_avg_label: () => <KPI label="Avg value / Label" value={d.avg_label_value != null ? `₹${d.avg_label_value.toFixed(2)}` : "—"} sub={`${formatShort(d.labels_billed)} labels billed`} testId="kpi-avg-label" to={linkIf("/sales")} />,
    kpi_dispatched: () => <KPI label="Dispatched" value={d.status_counts?.dispatched || 0} icon={Truck} testId="kpi-dispatched" to={linkIf("/orders", "/orders?status=dispatched")} />,
    // Printing desk tiles — same "click for the list" shape as Partially Dispatched, but jobs-by-status
    // driven (JobStatusTile) rather than a dedicated endpoint. Scheduled Jobs is look-ahead only
    // (linkable=false): visible for awareness, never actually openable (server enforces that too).
    kpi_ready_to_print: () => <JobStatusTile status="in_production" title="Ready to print" icon={Printer} testId="kpi-ready-to-print" />,
    kpi_scheduled_jobs: () => <JobStatusTile status="pending,to_be_planned,converted_bpr,planning" title="Scheduled Jobs" icon={CalendarClock} testId="kpi-scheduled-jobs" linkable={false} />,
    // QC desk tiles — same shape. QC owns both Quality and COA, so Ready for COA is fully clickable.
    kpi_materials_qc: () => <MaterialsQcTile />,
    kpi_jobs_ready_for_qc: () => <JobStatusTile status="slitting" title="Jobs ready for QC" icon={ClipboardList} testId="kpi-jobs-ready-for-qc" />,
    kpi_ready_for_coa: () => <JobStatusTile status="packing" title="Ready for COA" icon={ClipboardList} testId="kpi-ready-for-coa" />,
    // Slitting desk tile — same shape. Slitting's prerequisite is AVT — status "avt" once it completes.
    kpi_ready_for_slitting: () => <JobStatusTile status="avt" title="Ready for Slitting" icon={ClipboardList} testId="kpi-ready-for-slitting" />,
    // AVT desk tile — same shape. AVT's prerequisite is Printing — status "printing" (or "flat_bed"
    // for jobs that went through Flatbed) once it completes.
    kpi_ready_for_inspection: () => <JobStatusTile status="printing,flat_bed" title="Ready for Inspection" icon={ClipboardList} testId="kpi-ready-for-inspection" />,
    // Packing desk tile — same shape. Packing's prerequisite is Quality — status "qc" once it completes.
    kpi_ready_for_packing: () => <JobStatusTile status="qc" title="Ready for Packing" icon={ClipboardList} testId="kpi-ready-for-packing" />,
    // Dispatch desk tile — same shape, alongside Partially Dispatched (Dispatch keeps that one, it's
    // their own work too). Dispatch's prerequisite is Packing/COA — status "ready_to_dispatch".
    kpi_ready_to_dispatch: () => <JobStatusTile status="ready_to_dispatch" title="Ready to Dispatch" icon={Truck} testId="kpi-ready-to-dispatch" />,
    // Operations Head's per-department tiles — every job currently sitting at that department
    // (same status groups as job_status.py's ROLE_READY_STATUSES). Clicking one jumps straight into
    // Scheduling Jobs filtered to it, where the job can actually be reordered/marked urgent — unlike
    // every other department's tiles, which only ever look at one desk's own queue.
    kpi_dept_stores: () => deptTile("stores", Boxes),
    kpi_dept_printing: () => deptTile("printing", Printer),
    kpi_dept_avt: () => deptTile("avt", ClipboardCheck),
    kpi_dept_slitting: () => deptTile("slitting", Scissors),
    kpi_dept_quality: () => deptTile("quality", ShieldCheck),
    kpi_dept_packing: () => deptTile("packing", PackageCheck),
    kpi_dept_dispatch: () => deptTile("dispatch", Truck),
    kpi_jobs_dispatched: () => <JobStatusTile status="dispatched" title="Jobs Dispatched" icon={Truck} testId="kpi-jobs-dispatched" />,
    kpi_to_be_planned: () => <KPI label="To Be Planned" value={d.status_counts?.to_be_planned || 0} icon={CalendarClock} accent={(d.status_counts?.to_be_planned || 0) > 0 ? "text-amber-700" : "text-neutral-900"} testId="kpi-to-be-planned" to={linkIf("/orders", "/orders?status=to_be_planned")} />,
    // "Pending" = not planned yet. Once planning converts it to a BPR it is Stores' job ("Ready for Stores").
    kpi_lowstock: () => (
      <KPI
        label="Low Stock Alerts"
        value={d.low_stock_count}
        sub={`of ${d.materials_count} materials`}
        icon={AlertTriangle}
        accent={d.low_stock_count > 0 ? "text-amber-700" : "text-neutral-900"}
        testId="kpi-low-stock"
        to={linkIf("/inventory", "/inventory?tab=stock&status=ORDER")}
      />
    ),
  };
  // Money widgets are dropped for roles without finance access, whatever the config says.
  const kpiList = kpis.filter((k) => showFinance || !["kpi_revenue", "kpi_profit", "kpi_avg_invoice", "kpi_avg_job", "kpi_avg_label"].includes(k.id));
  const showTrend = has("trend") && showFinance && d.monthly_trend;
  const showStatus = has("job_status");
  const showRecent = has("recent_jobs");
  const showLow = has("low_stock");
  const byStatus = widgets.filter((w) => w.id === "jobs_by_status");
  // every dashboard that deals with jobs carries the part-dispatched tile (not HR's roster or purchasing's stock view)
  // Printing has its own Ready to Print / Scheduled Jobs tiles instead — dispatch status isn't its concern.
  const showPartialDispatch = !["hr", "purchase_officer", "printing_operator", "qc_inspector", "slitting_operator", "avt_operator", "packing_operator"].includes(user.role);

  return (
    <div>
      <PageHeader subtitle={cfg.subtitle} title={cfg.title} testId="dashboard-header" />
      <div className="p-8 space-y-6" data-testid={`dashboard-${user.role}`}>
        {has("orders_search") && <OrdersSearchBar />}
        {has("quick_links") && <QuickLinks links={view.quickLinks} />}
        {has("stores_dashboard") && <StoresDashboard />}

        {(kpiList.length > 0 || showPartialDispatch) && (
          <section className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3">
            {kpiList.map((k) => <div key={k.id}>{KPI_DEFS[k.id]()}</div>)}
            {showPartialDispatch && <div><PartialDispatch /></div>}
          </section>
        )}

        {has("qc_materials") && <QcMaterials />}
        {has("production_analytics") && <ProductionAnalytics />}
        {has("calibration_due") && <CalibrationDue />}

        {needsChurn && churn && (churn.dropped_by_half_or_more > 0 || churn.fully_dropped > 0) && (
          <section
            data-testid="churn-alert"
            className={`border p-5 flex flex-wrap items-center justify-between gap-4 ${
              churn.fully_dropped >= 3 ? "border-rose-300 bg-rose-50" : "border-amber-300 bg-amber-50"
            }`}
          >
            <div className="flex items-center gap-3">
              <UserMinus size={20} className={churn.fully_dropped >= 3 ? "text-rose-700" : "text-amber-700"} />
              <div>
                <div className="label-caps text-[10px] mb-0.5">Customer Churn Alert · Month-over-Month</div>
                <div className="text-sm text-neutral-900">
                  <span className="font-bold font-mono-num">{churn.dropped_by_half_or_more}</span> customer
                  {churn.dropped_by_half_or_more !== 1 && "s"} dropped by 50% or more
                  {churn.fully_dropped > 0 && (
                    <>
                      {" — "}
                      <span className={`font-bold font-mono-num ${churn.fully_dropped >= 3 ? "text-rose-700" : "text-amber-700"}`}>
                        {churn.fully_dropped}
                      </span>{" "}
                      fully stopped ordering
                    </>
                  )}
                </div>
                <div className="text-[11px] text-neutral-600 mt-0.5 font-mono-num">
                  {churn.baseline_period.from} → {churn.baseline_period.to} vs {churn.current_period.from} → {churn.current_period.to}
                </div>
              </div>
            </div>
            <Link to="/customers" data-testid="churn-alert-link" className="text-xs uppercase tracking-widest font-semibold text-[#FF5A00] hover:underline">
              View churn report →
            </Link>
          </section>
        )}
        {needsChurn && churn && churn.baseline_customers > 0 && churn.dropped_by_half_or_more === 0 && (
          <section data-testid="churn-ok" className="border border-emerald-200 bg-emerald-50/50 p-4 text-sm text-emerald-900 flex items-center gap-3">
            <TrendingUp size={16} />
            No customer dropped by 50%+ this month — retention looks stable across {churn.baseline_customers} customers.
          </section>
        )}

        {byStatus.length > 0 && (
          <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {byStatus.map((w) => <JobsByStatus key={w.status} status={w.status} title={w.title} readOnly={w.readOnly} />)}
          </section>
        )}

        {(showStatus || showTrend) && (
          <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {showStatus && (
              <div className={`${showTrend ? "lg:col-span-1" : "lg:col-span-3"} border border-neutral-200 bg-white p-5`}>
                <div className="label-caps mb-4">Job Status</div>
                <div className="space-y-2.5">
                  {/* Every status, as the Jobs Pipeline pivot lists them — none folded away. */}
                  {(d.status_breakdown || []).map((s) => {
                    const to = s.status && linkIf("/orders", `/orders?status=${encodeURIComponent(s.status)}`);
                    const row = (
                      <>
                        <StatusBadge status={s.status} />
                        <span className="font-mono-num text-sm font-semibold">{s.count.toLocaleString("en-IN")}</span>
                      </>
                    );
                    return to ? (
                      <Link key={s.key} to={to} title={`Open ${s.status.replace(/_/g, " ")} jobs`} data-testid={`status-count-${s.key}`}
                        className="flex items-center justify-between -mx-2 px-2 py-0.5 rounded-sm hover:bg-neutral-50">
                        {row}
                      </Link>
                    ) : (
                      <div key={s.key} className="flex items-center justify-between" data-testid={`status-count-${s.key}`}>{row}</div>
                    );
                  })}
                </div>
              </div>
            )}
            {showTrend && (
              <div className={`${showStatus ? "lg:col-span-2" : "lg:col-span-3"} border border-neutral-200 bg-white p-5`} data-testid="chart-monthly-trend">
                <div className="label-caps mb-4">Revenue vs Cost · Last 6 mo</div>
                <div className="h-64">
                  <ResponsiveContainer>
                    <BarChart data={d.monthly_trend} margin={{ top: 20, right: 8, left: 0, bottom: 0 }}>
                      <CartesianGrid stroke="#eee" strokeDasharray="2 2" vertical={false} />
                      <XAxis dataKey="month" stroke="#71717A" fontSize={11} />
                      <YAxis stroke="#71717A" fontSize={11} tickFormatter={formatINRAxis} />
                      <Tooltip formatter={(v) => `${formatINRShort(v)} · ${formatINR(v)}`} contentStyle={{ borderRadius: 2, border: "1px solid #E4E4E7", fontSize: 12 }} />
                      <Bar dataKey="revenue" fill="#FF5A00" name="Revenue">
                        <LabelList dataKey="revenue" content={barValue} />
                      </Bar>
                      <Bar dataKey="cost" fill="#3F3F46" name="Cost">
                        <LabelList dataKey="cost" content={barValue} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </section>
        )}

        {(showRecent || showLow) && (
          <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {showRecent && (
              <div className={`${showLow ? "lg:col-span-2" : "lg:col-span-3"} border border-neutral-200 bg-white`}>
                <div className="px-5 py-4 border-b border-neutral-200 flex justify-between items-center">
                  <div className="label-caps">Recent Jobs</div>
                  <Link to="/orders" className="text-xs text-[#FF5A00] hover:underline">View all →</Link>
                </div>
                <table className="w-full text-sm">
                  <thead className="bg-neutral-50 label-caps">
                    <tr>
                      <th className="text-left px-4 py-2.5">Job #</th>
                      <th className="text-left px-4 py-2.5">Customer</th>
                      <th className="text-left px-4 py-2.5">Status</th>
                      {showFinance && <th className="text-right px-4 py-2.5">Revenue</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {(d.recent_jobs || []).map((j) => (
                      <tr key={j.id} className={`border-t border-neutral-100 data-row ${canAccess(user, "/orders") ? "cursor-pointer" : ""}`}
                        onClick={(e) => canAccess(user, "/orders") && !e.target.closest("a") && nav(`/orders/${j.id}`)}>
                        <td className="px-4 py-2.5 font-mono-num text-xs">
                          <span className="inline-flex items-center gap-1.5"><JobLink id={j.id} number={j.mpwo_number || j.job_number} /><LotTag job={j} /></span>
                        </td>
                        <td className="px-4 py-2.5">{j.customer_name}</td>
                        <td className="px-4 py-2.5"><StatusBadge status={badgeStatus(j)} /></td>
                        {showFinance && <td className="px-4 py-2.5 text-right font-mono-num" title={formatINR(j.revenue)}>{formatINRShort(j.revenue)}</td>}
                      </tr>
                    ))}
                    {(d.recent_jobs || []).length === 0 && (
                      <tr><td colSpan={showFinance ? 4 : 3} className="px-4 py-6 text-center text-neutral-400 text-sm">No jobs yet. Create one from Orders.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
            {showLow && (
              <div className={`${showRecent ? "" : "lg:col-span-3"} border border-neutral-200 bg-white`}>
                <div className="px-5 py-4 border-b border-neutral-200 flex justify-between items-center">
                  <div className="label-caps">Low Stock</div>
                  <Package size={14} className="text-neutral-400" />
                </div>
                <div className="divide-y divide-neutral-100">
                  {(d.low_stock_items || []).map((m) => {
                    const to = stockFor(m.sku);
                    const Row = to ? Link : "div";
                    return (
                      <Row key={m.id} {...(to && { to })} className={`px-5 py-3 flex justify-between items-center ${to ? "hover:bg-neutral-50" : ""}`}>
                        <div>
                          <div className="text-sm font-medium">{m.name}</div>
                          <div className="text-xs text-neutral-500 font-mono-num">{m.sku}</div>
                        </div>
                        <div className="text-right">
                          <div className="font-mono-num text-sm font-semibold text-amber-700">{m.current_stock} {m.unit}</div>
                          <div className="text-[10px] text-neutral-500">reorder @ {m.reorder_level}</div>
                        </div>
                      </Row>
                    );
                  })}
                  {(!d.low_stock_items || d.low_stock_items.length === 0) && (
                    <div className="px-5 py-6 text-center text-neutral-400 text-sm">All stock healthy.</div>
                  )}
                </div>
              </div>
            )}
          </section>
        )}

        {has("operator_roster") && <OperatorRoster />}
      </div>
    </div>
  );
}
